import { Links } from 'application/constants/links';
import { TreeItems } from 'dnd-kit-sortable-tree';
import React, { useEffect, useMemo, useState } from 'react';
import { QueryFactory } from 'services/api';
import {
  ImportStatus,
  OperationType,
  ProjectResourceType,
  Status,
  VariableListItemDto,
} from 'services/api/api-client';
import DocumentIcon from 'assets/icons/document.svg?react';
import FolderIcon from 'assets/icons/folder.svg?react';
import DocumentsPlaceholder from 'assets/icons/documents-placeholder.svg?react';
import HintIcon from 'assets/icons/hint.svg?react';
import { AppMenu, AppMenuItem } from 'components/uikit/menu/AppMenu';
import styles from './DocumentList.module.scss';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { useNavigate } from 'react-router';
import { MenuDirection } from 'components/uikit/menu/MenuDirection';
import { DocumentTreeItem } from './listItem/DocumentTreeItem';
import { AddFolderModal } from './addFolder/AddFolderModal';
import {
  checkIsExpandDisabled,
  expandAll,
  isItemExported,
  isSameDepth,
  setItemUnexported,
  UpdateDocumentItemsProps,
  updateDocumentTreeProperties,
} from 'helpers/documents/documentsObserver';
import { TextButtonColor } from 'components/uikit/buttons/TextButton';
import MenuEye from 'assets/icons/menu-read-only-eye.svg?react';
import MenuEditable from 'assets/icons/menu-editable.svg?react';
import { Loading } from 'components/uikit/suspense/Loading';
import clsx from 'clsx';
import { LinearProgress } from '@mui/material';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import {
  createEditableDocuments,
  createReadOnlyDocuments,
  selectFiles,
} from 'helpers/documents/fileHandler';
import { useModal } from 'components/uikit/modal/useModal';
import { ItemChangedReason } from 'dnd-kit-sortable-tree/dist/types';
import { ProjectCopyDropdownModal } from './projectCopyDropdownModal/ProjectCopyDropdownModal';
import { ProjectImportFromSharePointModal } from './projectImportFromSharePointModal/ProjectImportFromSharePointModal';
import { assertNever } from 'helpers/assert-never';
import { BrightTooltip } from 'components/tooltips/BrightTooltip.tsx';
import {
  cloneNodes,
  NodeType,
  parseProjectElements,
} from 'helpers/documents/types/base';
import {
  BaseDocumentTreeNode,
  DocumentFolderNode,
  DocumentTreeNode,
  FolderStatus,
  isDocument,
} from 'helpers/documents/types/documents';
import { TreeControlPanel } from 'components/documents/TreeControlPanel';
import { useDocumentTree } from 'helpers/documents/useDocumentTree';
import { useTreeSearch } from 'helpers/documents/useTreeSearch';
import { useTreeActions } from 'helpers/documents/useTreeActions';
import { FolderList } from 'components/documents/FolderList';
import { Filter } from 'components/uikit/filters/Filter';
import { HeaderSelectModeCheckbox } from './selectMode/checkbox/SelectModeCheckbox';
import {
  SelectModeProvider,
  useSelectModeContext,
} from './selectMode/useSelectModeContext';
import { SelectModeOverlay } from './selectMode/SelectModeOverlay';
import { useGridTemplate } from 'helpers/useGridTemplate';
import { showErrorToast } from 'components/uikit/toast/showToast';
import { PermissionFabButton } from 'helpers/projectPermissions/components/PermissionFabButton.tsx';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import { PermissionTextButton } from 'helpers/projectPermissions/components/PermissionTextButton.tsx';
import { useHasProjectPermission } from 'helpers/projectPermissions/useHasProjectPermission.ts';
import { useVariablesContext } from 'pages/authorized/variables/variablesContext/VariablesContext';

const checkImportProgressTimeoutMs = 10000;

type DocumentListProps = {
  projectId: number;
  revisionId: number;
  revisionVariables: VariableListItemDto[];
};

export const DocumentList: React.FC<DocumentListProps> = (props) => {
  const params = Links.Authorized.ProjectDetails.useParams();
  const queryClient = useQueryClient();
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false);
  const { refreshVariables } = useVariablesContext();

  const documentQuery = QueryFactory.DocumentQuery.useSearchQuery({
    revisionId: props.revisionId === null ? undefined : props.revisionId,
  });

  const revisionQuery = QueryFactory.RevisionQuery.useGetQuery(
    params.queryParams.releaseId!,
    {
      refetchInterval: refetchInterval,
    },
  );
  const isImporting =
    revisionQuery.data?.sharePointImportStatus === ImportStatus.InProgress;

  useEffect(() => {
    if (!isImporting) {
      setRefetchInterval(false);
      void invalidateRevisionDependencies(queryClient);
    } else {
      setRefetchInterval(checkImportProgressTimeoutMs);
    }
  }, [isImporting]);

  const navigate = useNavigate();
  const i18n = useScopedTranslation(
    'Page.Projects.ProjectDetailsPage.FoldersTree',
  );
  const modalTranslation = useScopedTranslation('Component.CommonModals');
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isProjectCopyModalOpen, setIsProjectCopyModalOpen] = useState(false);
  const [
    isProjectImportFromSharePointModalOpen,
    setIsProjectImportFromSharePointModalOpen,
  ] = useState(false);
  const i18nStatus = useScopedTranslation(
    'Page.Projects.ProjectDetailsPage.Status',
  );

  const invalidateRevisionWithDependencies = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: QueryFactory.RevisionQuery.getQueryKey(props.revisionId),
      }),
      queryClient.invalidateQueries({
        queryKey: QueryFactory.DocumentQuery.searchQueryKey(props.revisionId),
      }),
      refreshVariables(),
      queryClient.invalidateQueries({
        queryKey:
          QueryFactory.DocumentSigningQuery.getDocumentsSigningStatusQueryKey(
            props.revisionId,
          ),
      }),
    ]);
  };

  const invalidateRevisionDependencies = async (queryClient: QueryClient) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
      }),
      refreshVariables(),
    ]);
  };

  const {
    items,
    setItems,
    itemsRef,
    onChangeItems,
    onChangeItemsWithInvalidation,
  } = useDocumentTree<BaseDocumentTreeNode>({
    onPatch: async (cleanItems) => {
      await QueryFactory.RevisionClient.patch(props.revisionId, {
        folders: cleanItems,
      });
    },
    onInvalidate: invalidateRevisionWithDependencies,
  });

  const selectedStatuses = params.queryParams.statusFilters as Status[];

  const { searchingName, setSearchingName, filteredItems } = useTreeSearch(
    items,
    selectedStatuses,
    params.queryParams.onlyNotUpdatedVersion ?? false,
  );

  const {
    isLoading,
    setIsLoading,
    onItemRemove,
    onItemEdit,
    onAddSubItem,
    onBulkRemove,
  } = useTreeActions({
    itemsRef,
    onChangeItemsWithInvalidation,
    onNodeDelete: async (node) => {
      if (isDocument(node))
        await QueryFactory.DocumentClient.delete_(node.documentId);
    },
  });

  const isExpandDisabled = checkIsExpandDisabled(items);
  const confirmMoveModal = useModal();

  const menuItems: AppMenuItem[] = useMemo(() => {
    const createSeparatingItem: AppMenuItem = {
      key: 'create',
      text: i18n.t('create'),
      className: styles.separatingMenuItem,
      onClick: () => {},
    };
    const newDocumentItem: AppMenuItem = {
      key: 'new_document',
      text: i18n.t('new_document'),
      icon: <DocumentIcon className={styles.contextIcon} />,
      className: styles.menuItem,
      onClick: () => {
        navigate(
          Links.Authorized.CreateDocument.link({
            projectId: props.projectId,
            releaseId: props.revisionId,
          }),
        );
      },
    };
    const newFolderItem: AppMenuItem = {
      key: 'new_folder',
      text: i18n.t('new_folder'),
      icon: <FolderIcon className={styles.contextIcon} />,
      className: styles.menuItem,
      onClick: () => setIsModalOpen(true),
    };
    const importSeparatingItem: AppMenuItem = {
      key: 'import',
      text: i18n.t('import'),
      className: clsx(styles.separatingMenuItem, styles.borderTop),
      onClick: () => {},
    };
    const importReadOnlyFile: AppMenuItem = {
      key: 'import_read_only',
      text: i18n.t('import_read_only'),
      icon: <MenuEye className={styles.contextIcon} />,
      className: styles.menuItem,
      onClick: async () => {
        const files = await selectFiles(false, true);
        setIsLoading(true);
        setShowContextualMenu(false);
        try {
          if (revisionQuery.data) {
            await createReadOnlyDocuments(
              files,
              revisionQuery.data.id,
              undefined,
              invalidateRevisionWithDependencies,
            );
          }
        } finally {
          setIsLoading(false);
        }
      },
    };
    const importEditableFile: AppMenuItem = {
      key: 'import_editable',
      text: i18n.t('import_editable'),
      icon: <MenuEditable className={styles.contextIcon} />,
      className: styles.menuItem,
      onClick: async () => {
        const files = await selectFiles(true, true);
        setIsLoading(true);
        setShowContextualMenu(false);
        try {
          if (revisionQuery.data) {
            await createEditableDocuments(
              files,
              revisionQuery.data.id,
              undefined,
              invalidateRevisionWithDependencies,
            );
          }
        } finally {
          setIsLoading(false);
        }
      },
    };

    return [
      createSeparatingItem,
      newDocumentItem,
      newFolderItem,
      importSeparatingItem,
      importEditableFile,
      importReadOnlyFile,
    ];
  }, [i18n, navigate, props.projectId, props.revisionId]);

  const onShowContextualMenu = React.useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.stopPropagation();
      ev.preventDefault();
      setShowContextualMenu(true);
    },
    [],
  );

  const onHideContextualMenu = React.useCallback(
    () => setShowContextualMenu(false),
    [],
  );

  const confirmBeforeMove = async (
    changedItems: TreeItems<BaseDocumentTreeNode>,
    reason: ItemChangedReason<BaseDocumentTreeNode>,
  ) => {
    const draggedItem = reason.type === 'dropped' ? reason.draggedItem : null;
    const fromParent =
      reason.type === 'dropped' ? reason.draggedFromParent : null;
    const toParent = reason.type === 'dropped' ? reason.droppedToParent : null;

    const shouldConfirm =
      draggedItem &&
      isItemExported(draggedItem!) &&
      !isSameDepth(fromParent, toParent);

    if (shouldConfirm) {
      setIsLoading(true);
      const confirmed = await confirmMoveModal.showConfirm({
        title: modalTranslation.t('confirm_change'),
        text: modalTranslation.t('confirm_change_message'),
      });
      if (confirmed) {
        await setItemUnexported(draggedItem!);
        await onChangeItemsWithInvalidation(changedItems);
      }
    } else {
      await onChangeItems(changedItems, hasUpdateRevisionPermission);
    }
    setIsLoading(false);
  };

  const updateProps: UpdateDocumentItemsProps = {
    revisionId: props.revisionId!,
    projectId: props.projectId!,
    onDelete: onItemRemove,
    onItemEdit: onItemEdit,
    onAddSubItem: onAddSubItem,
  };

  const Footer = () => {
    return (
      <div>
        <div className={styles.footer} />
        <AddFolderModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            onHideContextualMenu();
          }}
          createFolderNode={async (name, id) => {
            const items_copy = cloneNodes(items);
            items_copy.push({
              id,
              type: NodeType.Folder,
              name,
              folderStatus: FolderStatus.NotStarted,
              children: [],
              projectId: params.projectId,
              revisionId: params.queryParams.releaseId!,
              collapsed: true,
              onDelete: async (folderId) => await onItemRemove(folderId),
              onFolderEdit: async (folderId, editedName, nameKey) =>
                await onItemEdit(folderId, editedName, nameKey),
              onAddSubItem: async (folderId, node) => {
                await onAddSubItem(folderId, node);
              },
            } as DocumentFolderNode);

            await onChangeItemsWithInvalidation([...items_copy]);
          }}
        />
        <ProjectCopyDropdownModal
          isOpen={isProjectCopyModalOpen}
          currentProjectId={revisionQuery.data?.projectId}
          onClose={() => setIsProjectCopyModalOpen(false)}
          onCopy={async (projectId) => {
            if (projectId) {
              try {
                await QueryFactory.RevisionClient.copyFromProject(
                  revisionQuery.data?.id,
                  projectId,
                );
                await invalidateRevisionWithDependencies();
              } catch (e) {
                showErrorToast(e);
                return false;
              }
            }
            return true;
          }}
        />
        {revisionQuery.data?.id && (
          <ProjectImportFromSharePointModal
            isOpen={isProjectImportFromSharePointModalOpen}
            currentRevisionId={revisionQuery.data.id}
            onClose={() => setIsProjectImportFromSharePointModalOpen(false)}
          />
        )}
      </div>
    );
  };

  const { grid, hiddenColumns, hideableColumns } = useGridTemplate(
    props.projectId,
    'DocumentList',
  );

  const Header = () => {
    return (
      <div
        className={items.length > 0 ? styles.header : styles.emptyTreeHeader}
        style={{ gridTemplateColumns: grid }}
      >
        <HeaderSelectModeCheckbox />
        <div className={styles.headerName}>{i18n.t('name')}</div>
        <div
          className={clsx(styles.column, {
            [styles.columnHidden]: hiddenColumns.includes('status'),
          })}
        >
          {i18n.t('status')}
        </div>
        <div
          className={clsx(styles.column, styles.version, {
            [styles.columnHidden]: hiddenColumns.includes('version'),
          })}
        >
          {i18n.t('version')}
        </div>
        <div
          className={clsx(styles.column, {
            [styles.columnHidden]: hiddenColumns.includes('date'),
          })}
        >
          {i18n.t('date')}
        </div>
        <div
          className={clsx(styles.column, styles.sign, {
            [styles.columnHidden]: hiddenColumns.includes('sign'),
          })}
        >
          {i18n.t('sign')}
          <BrightTooltip
            key={'sign'}
            title={
              <div className={styles.tooltip}>
                <div className={styles.tooltipText}>
                  {i18n.t('sign_tooltip_text')}
                </div>
                <div className={styles.tooltipComment}>
                  {i18n.t('sign_tooltip_comment')}
                </div>
              </div>
            }
            placement="top"
          >
            <div className={styles.signTooltip}>
              <HintIcon />
            </div>
          </BrightTooltip>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!documentQuery.data?.data) return;
    if (!revisionQuery?.data) return;

    const fetchedItems = parseProjectElements(
      revisionQuery.data.folders,
      NodeType.Document,
    ) as BaseDocumentTreeNode[];

    if (fetchedItems.length > 0) {
      setItems(
        updateDocumentTreeProperties(
          fetchedItems,
          documentQuery.data.data,
          updateProps,
        ),
      );
    } else {
      setItems([]);
    }
  }, [revisionQuery.data?.folders, documentQuery.data?.data]);

  const hasPermission = useHasProjectPermission();
  const hasUpdateRevisionPermission = hasPermission([
    permission(ProjectResourceType.Revision, OperationType.Update),
  ]);

  return (
    <div className={styles.mainContainer} data-test-id="document-list">
      <Loading
        className={styles.loader}
        loadingDataClassName={styles.loadedContent}
        loading={
          isLoading || documentQuery.isLoading || revisionQuery.isLoading
        }
      >
        <SelectModeProvider items={filteredItems}>
          <TreeControlPanel
            isExpandDisabled={isExpandDisabled}
            searchValue={searchingName}
            onSearchChange={setSearchingName}
            onExpand={async (isExpanded) => {
              expandAll(itemsRef.current, isExpanded);
              await onChangeItems(
                itemsRef.current,
                hasUpdateRevisionPermission,
              );
            }}
            projectId={props.projectId}
            tableColumns={hideableColumns.map((value) => ({
              value,
              label: i18n.t(value),
            }))}
            enableSelectMode={true}
            additionalFilters={
              <Filter
                options={Object.keys(Status).map((key) => ({
                  value: Status[key as keyof typeof Status],
                  localization: i18nStatus.t(
                    `DocumentStatus.${Status[key as keyof typeof Status]}`,
                  ),
                }))}
                selectedOptions={selectedStatuses}
                onValueChanged={(selectedStatuses) => {
                  params.setQueryParams({ statusFilters: selectedStatuses });
                }}
                placeholder={i18nStatus.t('title')}
              />
            }
          />
          <div className={styles.treeContainer}>
            <FolderList<DocumentTreeNode>
              items={filteredItems as DocumentTreeNode[]}
              showNoItemsPlaceholder={
                !isLoading &&
                !documentQuery.isLoading &&
                !revisionQuery.isLoading
              }
              onChangeItems={
                selectedStatuses.length > 0 || searchingName !== ''
                  ? async () => {}
                  : async (items, reason) =>
                      await confirmBeforeMove(items, reason)
              }
              footer={<Footer />}
              header={<Header />}
              listItem={DocumentTreeItem}
              styles={{
                tree: styles.tree,
                scrollWrapper: styles.scrollWrapper,
              }}
              noItemsPlaceholder={
                <div className={styles.placeHolderContainer}>
                  <DocumentsPlaceholder className={styles.placeHolder} />
                  {items.length == 0 && (
                    <>
                      <PermissionTextButton
                        testId="copy-from-project"
                        title={i18n.t('copy_revision')}
                        color={TextButtonColor.Primary}
                        className={styles.copyButton}
                        onClick={() => setIsProjectCopyModalOpen(true)}
                        permissions={[
                          permission(
                            ProjectResourceType.Revision,
                            OperationType.Update,
                          ),
                        ]}
                      />
                      {(() => {
                        const status =
                          revisionQuery.data?.sharePointImportStatus;
                        if (!status) {
                          return <></>;
                        }
                        switch (status) {
                          case ImportStatus.NotStarted:
                            return (
                              <PermissionTextButton
                                testId="import-from-sharepoint"
                                title={i18n.t(
                                  'import_revision_from_sharepoint',
                                )}
                                color={TextButtonColor.Primary}
                                className={styles.copyButton}
                                onClick={() =>
                                  setIsProjectImportFromSharePointModalOpen(
                                    true,
                                  )
                                }
                                permissions={[
                                  permission(
                                    ProjectResourceType.Revision,
                                    OperationType.Update,
                                  ),
                                ]}
                              />
                            );
                          case ImportStatus.InProgress:
                            return (
                              <>
                                <LinearProgress
                                  color="success"
                                  sx={{ width: '100%', marginTop: 1 }}
                                />
                                <div className={styles.importLoaderText}>
                                  {i18n.t('import_in_progress')}
                                </div>
                              </>
                            );
                          case ImportStatus.Imported:
                            return <></>;
                          default:
                            assertNever(status);
                        }
                      }).call(this)}
                    </>
                  )}
                </div>
              }
            />
            <DocumentFabButton
              menuItems={menuItems}
              showContextualMenu={showContextualMenu}
              onShowContextualMenu={onShowContextualMenu}
              onHideContextualMenu={onHideContextualMenu}
            />
          </div>
          {documentQuery.data?.data && (
            <SelectModeOverlay
              revisionId={props.revisionId}
              projectId={props.projectId}
              setIsLoading={setIsLoading}
              onBulkRemove={onBulkRemove}
              revisionDocuments={documentQuery.data.data}
              revisionVariables={props.revisionVariables}
            />
          )}
        </SelectModeProvider>
      </Loading>
    </div>
  );
};

const DocumentFabButton = (props: {
  menuItems: AppMenuItem[];
  showContextualMenu: boolean;
  onShowContextualMenu: (ev: React.MouseEvent<HTMLElement>) => void;
  onHideContextualMenu: () => void;
}) => {
  const linkRef = React.useRef<HTMLElement>(null);
  const { isSelectMode } = useSelectModeContext();

  return (
    <div className={styles.fab} data-select-mode={isSelectMode}>
      <a
        onClick={(e) => props.onShowContextualMenu(e)}
        href="#"
        ref={linkRef as any}
      >
        <PermissionFabButton
          testId="add-item-button"
          className={clsx(props.showContextualMenu && styles.fabButton)}
          permissions={[
            permission(ProjectResourceType.Document, OperationType.Create),
            permission(ProjectResourceType.Revision, OperationType.Update),
          ]}
        />
      </a>
      <AppMenu
        menuItems={props.menuItems}
        open={props.showContextualMenu}
        anchorEl={linkRef.current}
        className={styles.appMenu}
        onClose={props.onHideContextualMenu}
        direction={MenuDirection.topCenter}
      />
    </div>
  );
};
